const uxuiCheatSheet = {
  id: "uxui-cheatsheet",
  title: "UX/UI Design Cheatsheet",
  description:
    "A comprehensive guide to user experience and interface design principles, patterns, and best practices across various platforms.",
  category: "Design",
  level: "All Levels",
  sections: [
    {
      title: "Device Mockups",
      content: [
        {
          title: "Responsive Device Showcase",
          content:
            "Interactive device mockups showing how designs adapt across different screen sizes.",
          code: `<div class="device-showcase">
  <div class="device mobile">
    <div class="screen">
      <img src="/images/mobile-mockup.png" alt="Mobile view" />
    </div>
  </div>
  <div class="device tablet">
    <div class="screen">
      <img src="/images/tablet-mockup.png" alt="Tablet view" />
    </div>
  </div>
  <div class="device desktop">
    <div class="screen">
      <img src="/images/desktop-mockup.png" alt="Desktop view" />
    </div>
  </div>
</div>`,
          notes:
            "Use device mockups to demonstrate responsive design principles and breakpoints.",
        },
        {
          title: "Interactive Prototype",
          content:
            "Clickable prototype demonstrating user flows and interactions.",
          code: `<div class="interactive-prototype">
  <div class="screen" data-state="home">
    <button class="nav-button" data-target="menu">Menu</button>
    <button class="action-button" data-target="product">View Product</button>
  </div>
</div>`,
          notes:
            "Create interactive prototypes to test user flows before development.",
        },
      ],
    },
    {
      title: "WCAG Compliance",
      content: [
        {
          title: "Accessibility Examples",
          content:
            "Interactive examples demonstrating WCAG compliance principles.",
          code: `<div class="accessibility-demo">
  <div class="contrast-example">
    <p class="good-contrast">Good contrast (WCAG AAA)</p>
    <p class="bad-contrast">Poor contrast (Fails WCAG)</p>
  </div>
  <div class="keyboard-nav">
    <button class="focusable">Focusable Element 1</button>
    <button class="focusable">Focusable Element 2</button>
  </div>
</div>`,
          notes:
            "Demonstrate proper contrast ratios, keyboard navigation, and ARIA attributes.",
        },
        {
          title: "Screen Reader Demo",
          content: "Example of proper screen reader markup and navigation.",
          code: `<nav aria-label="Main navigation">
  <ul role="menu">
    <li role="none">
      <a role="menuitem" href="#home">Home</a>
    </li>
    <li role="none">
      <a role="menuitem" href="#products">Products</a>
    </li>
  </ul>
</nav>`,
          notes:
            "Show proper ARIA roles and landmarks for screen reader compatibility.",
        },
      ],
    },
    {
      title: "Color Theory",
      items: [
        {
          title: "Color Psychology",
          content:
            "Understanding emotional and psychological impacts of colors in design",
          component: "ColorPsychologyGrid",
          notes:
            "Click any color to copy its hex code. Hover to see detailed psychological impacts.",
        },
        {
          title: "Color Theory Guide",
          content:
            "Interactive guide showing color relationships and combinations",
          component: "ColorTheoryWheel",
          notes:
            "Explore color relationships, harmonies, and color mixing modes.",
        },
      ],
    },
    {
      title: "UX Design Process",
      items: [
        {
          title: "1. Empathize & Research",
          content: "Understanding users and their needs through research",
          code: `// Research Methods
1. User Interviews
   - One-on-one discussions
   - Contextual inquiries
   - Focus groups

2. Surveys & Questionnaires
   - Online surveys
   - Email questionnaires
   - In-app feedback forms

3. Analytics Review
   - User behavior data
   - Heat maps
   - Session recordings

4. Competitive Analysis
   - Direct competitors
   - Indirect competitors
   - Industry benchmarks`,
          notes:
            "Use a mix of qualitative and quantitative research methods. Document user pain points, needs, and behaviors.",
        },
        {
          title: "2. Define & Synthesize",
          content: "Analyzing research findings and defining the problem space",
          code: `// Problem Definition Tools
1. User Personas
   Name: Sarah Chen
   Age: 28
   Occupation: Digital Marketing Manager
   Goals: Streamline workflow, collaborate effectively
   Pain Points: Complex tools, scattered communications

2. User Journey Maps
   Awareness → Research → Trial → Purchase → Onboarding → Usage

3. Problem Statement
   "Marketing managers need a way to
    streamline their workflow because
    current tools are fragmented and
    time-consuming."

4. Success Metrics
   - Reduced task completion time
   - Increased user satisfaction
   - Higher retention rates`,
          notes:
            "Create clear, actionable problem statements. Use data to support your definitions.",
        },
        {
          title: "3. Ideate & Conceptualize",
          content: "Generating solutions and exploring possibilities",
          code: `// Ideation Techniques
1. Brainstorming Methods
   - Mind mapping
   - Crazy 8s
   - Design studio
   - Reverse thinking

2. Solution Sketching
   [Quick Sketch] → [Detailed Sketch] → [Solution Variants]
   
3. Feature Prioritization
   Must Have:
   - Core functionality
   - Essential features
   Should Have:
   - Important but not critical
   Could Have:
   - Nice to have features
   Won't Have:
   - Out of scope items`,
          notes:
            "Generate many ideas first, then narrow down based on feasibility and impact.",
        },
        {
          title: "4. Prototype & Design",
          content: "Creating testable solutions at various fidelities",
          code: `// Prototyping Levels
1. Low-Fidelity
   - Paper sketches
   - Wireframes
   - Basic interactions

2. Mid-Fidelity
   - Detailed wireframes
   - Interactive prototypes
   - Basic styling

3. High-Fidelity
   - Visual design
   - Animations
   - Full interactions

// Design System
Components:
├── Atoms (buttons, inputs)
├── Molecules (forms, cards)
├── Organisms (headers, sections)
└── Templates (layouts, pages)`,
          notes:
            "Start with low-fidelity prototypes and iterate based on feedback. Maintain consistency with a design system.",
        },
        {
          title: "5. Test & Validate",
          content: "Evaluating solutions with users and stakeholders",
          code: `// Testing Methods
1. Usability Testing
   - Task completion
   - Time on task
   - Error rates
   - User satisfaction

2. A/B Testing
   Version A → [Metrics] ← Version B
   - Conversion rates
   - Engagement
   - User preference

3. Feedback Collection
   - User interviews
   - Surveys
   - Analytics
   - Heatmaps

4. Iteration Planning
   [Test] → [Analyze] → [Iterate] → [Retest]`,
          notes:
            "Test early and often. Document findings and iterate based on user feedback.",
        },
        {
          title: "6. Implement & Monitor",
          content: "Launching solutions and tracking performance",
          code: `// Implementation Checklist
1. Design Handoff
   ├── Assets
   ├── Specifications
   ├── Interactions
   └── Documentation

2. Development Support
   - Design QA
   - Technical constraints
   - Browser testing

3. Performance Metrics
   - User engagement
   - Task success rates
   - Customer satisfaction
   - Business metrics

4. Continuous Improvement
   [Monitor] → [Analyze] → [Optimize]`,
          notes:
            "Work closely with development team. Monitor metrics and gather feedback for future improvements.",
        },
        {
          title: "Process Diagram",
          content: "Visual representation of the UX design process",
          component: "UXProcessDiagram",
          notes:
            "The process is iterative - each phase can lead back to previous phases based on findings and feedback.",
        },
      ],
    },
    {
      title: "Typography",
      items: [
        {
          title: "Type Scale",
          content: "Consistent type sizing using modular scale for hierarchy.",
          example: {
            type: "typography",
            data: [
              {
                size: "48px",
                weight: "800",
                text: "Display Large",
                lineHeight: "1.1",
              },
              {
                size: "40px",
                weight: "700",
                text: "Display Small",
                lineHeight: "1.2",
              },
              {
                size: "32px",
                weight: "700",
                text: "Heading 1",
                lineHeight: "1.2",
              },
              {
                size: "24px",
                weight: "600",
                text: "Heading 2",
                lineHeight: "1.3",
              },
              {
                size: "20px",
                weight: "600",
                text: "Heading 3",
                lineHeight: "1.4",
              },
              {
                size: "16px",
                weight: "400",
                text: "Body 1",
                lineHeight: "1.5",
              },
              {
                size: "14px",
                weight: "400",
                text: "Body 2",
                lineHeight: "1.5",
              },
              {
                size: "12px",
                weight: "400",
                text: "Caption",
                lineHeight: "1.5",
              },
            ],
          },
          notes:
            "Use a modular scale ratio (e.g., 1.2 or 1.5) for consistent visual rhythm. Maintain proper line height ratios for readability.",
        },
      ],
    },
    {
      title: "Layout & Grid",
      items: [
        {
          title: "Grid System",
          content: "Using grid layouts for consistent spacing and alignment.",
          example: {
            type: "grid",
            data: {
              columns: 12,
              gutter: "24px",
              margin: "16px",
              layouts: [
                { cols: [12], content: "Full width" },
                { cols: [6, 6], content: "Two columns" },
                { cols: [4, 4, 4], content: "Three columns" },
                { cols: [3, 3, 3, 3], content: "Four columns" },
                { cols: [8, 4], content: "Sidebar layout" },
                { cols: [2, 8, 2], content: "Content focus" },
              ],
            },
          },
          notes:
            "Use 8px or 4px as base unit for consistent spacing. Adapt grid columns based on content needs.",
        },
        {
          title: "Responsive Patterns",
          content:
            "Common responsive layout patterns for different screen sizes.",
          example: {
            type: "responsive",
            data: {
              mobile: { cols: 4, gutter: "16px", breakpoint: "320px - 767px" },
              tablet: { cols: 8, gutter: "24px", breakpoint: "768px - 1023px" },
              desktop: { cols: 12, gutter: "24px", breakpoint: "1024px+" },
            },
          },
          notes:
            "Design mobile-first, then progressively enhance for larger screens. Use consistent breakpoints across your design system.",
        },
      ],
    },
    {
      title: "Components",
      items: [
        {
          title: "Navigation Patterns",
          content: "Common navigation patterns for different device types.",
          example: {
            type: "navigation",
            data: {
              patterns: [
                {
                  name: "Tab Bar",
                  items: ["Home", "Search", "Profile", "Settings"],
                },
                {
                  name: "Hamburger Menu",
                  items: ["Dashboard", "Projects", "Team", "Settings", "Help"],
                },
              ],
            },
          },
          notes:
            "Keep navigation consistent across all pages and provide clear visual feedback",
        },
        {
          title: "Form Elements",
          content: "Best practices for form design and input elements.",
          example: {
            type: "form",
            data: {
              inputs: [
                { type: "text", label: "Username" },
                { type: "select", label: "Country" },
                { type: "checkbox", label: "Remember me" },
                { type: "radio", label: "Preferences" },
              ],
            },
          },
          notes:
            "Use clear labels, validation feedback, and appropriate input types",
        },
      ],
    },
    {
      title: "Spacing & Sizing",
      items: [
        {
          title: "Spacing Scale",
          content: "Consistent spacing values for margins and padding.",
          example: {
            type: "spacing",
            data: [
              { size: 4, label: "xs", use: "Fine details" },
              { size: 8, label: "sm", use: "Tight spacing" },
              { size: 16, label: "md", use: "Standard spacing" },
              { size: 24, label: "lg", use: "Component spacing" },
              { size: 32, label: "xl", use: "Section spacing" },
              { size: 48, label: "2xl", use: "Layout spacing" },
              { size: 64, label: "3xl", use: "Page spacing" },
            ],
          },
          notes:
            "Use consistent spacing to create visual hierarchy and improve readability. Base all spacing on a 4px or 8px grid.",
        },
      ],
    },
    {
      title: "Interactive States",
      items: [
        {
          title: "State Changes",
          content: "Visual feedback for different interaction states.",
          example: {
            type: "states",
            data: [
              { state: "Default", color: "#1a73e8", elevation: "0px" },
              { state: "Hover", color: "#1557b0", elevation: "2px" },
              { state: "Active", color: "#174ea6", elevation: "1px" },
              {
                state: "Focus",
                color: "#1a73e8",
                elevation: "0px",
                outline: "2px",
              },
              { state: "Disabled", color: "#dadce0", elevation: "0px" },
            ],
          },
          notes:
            "Provide clear visual feedback for all interactive elements. Use consistent state changes across components.",
        },
        {
          title: "Animation Timing",
          content: "Common animation duration and easing functions.",
          example: {
            type: "animation",
            data: [
              {
                duration: "100ms",
                easing: "ease-in-out",
                use: "Micro-interactions (clicks, toggles)",
              },
              {
                duration: "200ms",
                easing: "ease",
                use: "Standard transitions (hovers, reveals)",
              },
              {
                duration: "300ms",
                easing: "cubic-bezier(0.4, 0, 0.2, 1)",
                use: "Complex animations (modals, drawers)",
              },
              {
                duration: "400ms",
                easing: "cubic-bezier(0.4, 0, 0.2, 1)",
                use: "Large transitions (page transitions)",
              },
            ],
          },
          notes:
            "Keep animations subtle and purposeful. Avoid excessive motion that might cause discomfort. Hover over examples to see the animations in action.",
        },
      ],
    },
    {
      title: "Platform Guidelines",
      items: [
        {
          title: "iOS Design",
          content: "Apple Human Interface Guidelines specifications.",
          code: `// iOS Specifications
Status Bar: 44pt
Navigation Bar: 44pt
Tab Bar: 49pt
Toolbar: 44pt

// Typography
Large Title: 34pt
Title 1: 28pt
Title 2: 22pt
Title 3: 20pt
Body: 17pt

// Common Elements
Button Height: 44pt minimum
Table Row: 44pt minimum
Margins: 16pt standard`,
          note: "Follow Apple's HIG for consistent iOS experiences.",
          visualExamples: [
            {
              type: "ios-specs",
              title: "iOS Components",
              url: "https://developer.apple.com/design/resources/",
            },
          ],
        },
        {
          title: "Android Design",
          content: "Material Design specifications for Android.",
          code: `// Android Specifications
Status Bar: 24dp
App Bar: 56dp
Bottom Navigation: 56dp
FAB Size: 56dp

// Typography
Headline 1: 96sp
Headline 2: 60sp
Headline 3: 48sp
Body 1: 16sp
Body 2: 14sp

// Density Scales
Touch target: 48x48dp
Icon size: 24x24dp
List item: 72dp height`,
          note: "Follow Material Design guidelines for consistent Android experiences.",
          visualExamples: [
            {
              type: "material-specs",
              title: "Material Components",
              url: "https://material.io/components",
            },
          ],
        },
      ],
    },
    {
      title: "UX Principles",
      items: [
        {
          title: "Information Architecture",
          content: "Organizing and structuring content effectively.",
          code: `// Common IA Patterns
Hierarchical: Parent → Child relationships
Sequential: Step-by-step flows
Matrix: Tag-based organization
Flat: Simple, shallow structure

// Navigation Depth
Mobile: 3-4 levels maximum
Desktop: 4-5 levels maximum

// Menu Structure
Primary nav: 5-7 items
Dropdown: 7-10 items
Footer: 20-30 links maximum`,
          note: "Create clear, logical content hierarchies that match user mental models.",
          visualExamples: [
            {
              type: "ia-diagram",
              title: "Site Map Example",
              description: "Visual hierarchy of website structure",
            },
          ],
        },
        {
          title: "User Flow Design",
          content: "Designing effective user journeys and flows.",
          code: `// Flow Components
Start/End points
Decision points
Actions
System processes
User inputs

// Common Flows
Onboarding: 3-5 steps maximum
Checkout: 3-4 steps maximum
Registration: 1-2 steps preferred
Settings: Grouped by category`,
          note: "Design flows that are logical, efficient, and reduce user cognitive load.",
          visualExamples: [
            {
              type: "user-flow",
              title: "Checkout Flow Example",
              steps: ["Cart", "Shipping", "Payment", "Confirmation"],
            },
          ],
        },
      ],
    },
    {
      title: "Accessibility",
      items: [
        {
          title: "Color & Contrast",
          content: "Ensuring accessible color usage and contrast ratios.",
          code: `// WCAG Contrast Requirements
Normal Text: 4.5:1 minimum
Large Text: 3:1 minimum
UI Components: 3:1 minimum

// Color Blindness
Avoid red/green combinations
Use patterns with colors
Provide text alternatives

// Focus States
Visible focus indicators
Minimum 3px border
High contrast colors`,
          note: "Design for accessibility from the start. Test with screen readers and keyboard navigation.",
          visualExamples: [
            {
              type: "contrast-examples",
              title: "Contrast Ratios",
              examples: ["Good: 7:1", "Minimum: 4.5:1", "Poor: 2:1"],
            },
          ],
        },
      ],
    },
    {
      title: "Design Systems",
      items: [
        {
          title: "Component Library",
          content: "Building and maintaining a design system.",
          code: `// Component Structure
Atoms: Basic elements
Molecules: Simple components
Organisms: Complex components
Templates: Page layouts
Pages: Specific instances

// Documentation
Usage guidelines
Props & variants
Do's and don'ts
Code examples
Accessibility notes`,
          note: "Create consistent, reusable components that scale across products.",
          visualExamples: [
            {
              type: "design-system",
              title: "Component Library Example",
              url: "https://material.io/design/components/buttons.html",
            },
          ],
        },
      ],
    },
    {
      title: "Responsive Design",
      items: [
        {
          title: "Breakpoints",
          content: "Standard breakpoints for responsive design.",
          code: `// Common Breakpoints
Mobile: 320px - 480px
Tablet: 481px - 768px
Laptop: 769px - 1024px
Desktop: 1025px - 1200px
Large Desktop: 1201px+

// Component Adaptations
Cards: 1/2/3/4 columns
Images: Art direction
Typography: Fluid scaling
Navigation: Progressive disclosure`,
          note: "Design mobile-first and enhance for larger screens.",
          visualExamples: [
            {
              type: "responsive-grid",
              title: "Responsive Layout Examples",
              layouts: ["Mobile", "Tablet", "Desktop"],
            },
          ],
        },
      ],
    },
    {
      title: "UX Design Principles",
      items: [
        {
          title: "1. User-Centered Design",
          content: "Design that prioritizes user needs, goals, and behaviors.",
          examples: [
            {
              title: "User Research",
              description:
                "Conducting interviews, surveys, and usability testing to understand user needs",
              code: `// Example user research questions
const userResearchQuestions = {
  goals: "What are you trying to accomplish?",
  painPoints: "What frustrates you about current solutions?",
  behaviors: "How do you currently solve this problem?",
  preferences: "What features would make your life easier?"
};`,
            },
            {
              title: "User Personas",
              description:
                "Creating detailed user profiles to guide design decisions",
              code: `const userPersona = {
  name: "Sarah, 32",
  role: "Marketing Manager",
  goals: ["Increase team productivity", "Track campaign performance"],
  painPoints: ["Too many tools to manage", "Complex reporting"],
  behaviors: ["Uses mobile for quick updates", "Prefers visual data"]
};`,
            },
          ],
          notes:
            "Always start with user research and validate designs with real users.",
        },
        {
          title: "2. Consistency & Standards",
          content:
            "Maintaining consistent patterns and behaviors across the interface.",
          examples: [
            {
              title: "Design System",
              description: "Creating reusable components and patterns",
              code: `const buttonStyles = {
  primary: {
    backgroundColor: "#1a73e8",
    color: "#ffffff",
    padding: "12px 24px",
    borderRadius: "4px"
  },
  secondary: {
    backgroundColor: "#f8f9fa",
    color: "#202124",
    padding: "12px 24px",
    borderRadius: "4px"
  }
};`,
            },
            {
              title: "Navigation Patterns",
              description: "Consistent navigation structure across pages",
              code: `const navigation = {
  mainNav: [
    { label: "Home", icon: "home", path: "/" },
    { label: "Products", icon: "products", path: "/products" },
    { label: "About", icon: "info", path: "/about" }
  ],
  userNav: [
    { label: "Profile", icon: "user", path: "/profile" },
    { label: "Settings", icon: "settings", path: "/settings" }
  ]
};`,
            },
          ],
          notes: "Consistency reduces cognitive load and improves usability.",
        },
        {
          title: "3. Visibility & Feedback",
          content: "Making system status and actions clear to users.",
          examples: [
            {
              title: "Loading States",
              description: "Clear feedback during loading and processing",
              code: `const LoadingState = () => (
  <div className="loading-container">
    <Spinner />
    <p>Processing your request...</p>
    <ProgressBar value={75} />
  </div>
);`,
            },
            {
              title: "Success/Error Messages",
              description: "Clear feedback for user actions",
              code: `const FeedbackMessage = ({ type, message }) => (
  <div className={\`feedback \${type}\`}>
    <Icon type={type} />
    <p>{message}</p>
    {type === 'error' && <RetryButton />}
  </div>
);`,
            },
          ],
          notes:
            "Users should always know what's happening and what they can do next.",
        },
        {
          title: "4. Error Prevention & Recovery",
          content:
            "Designing to prevent errors and help users recover from them.",
          examples: [
            {
              title: "Form Validation",
              description: "Real-time validation and helpful error messages",
              code: `const FormValidation = () => {
  const [errors, setErrors] = useState({});
  
  const validateField = (field, value) => {
    const rules = {
      email: /^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/,
      password: /^(?=.*[A-Za-z])(?=.*\\d)[A-Za-z\\d]{8,}$/
    };
    
    return rules[field].test(value);
  };
};`,
            },
            {
              title: "Undo/Redo",
              description: "Allowing users to reverse actions",
              code: `const ActionHistory = () => {
  const [history, setHistory] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  
  const undo = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      return history[currentIndex - 1];
    }
  };
};`,
            },
          ],
          notes:
            "Prevent errors where possible, and make recovery easy when they occur.",
        },
        {
          title: "5. Recognition Over Recall",
          content:
            "Making information and actions visible rather than requiring memory.",
          examples: [
            {
              title: "Contextual Help",
              description: "Providing help where users need it",
              code: `const ContextualHelp = ({ context }) => (
  <div className="help-tooltip">
    <Icon type="help" />
    <div className="help-content">
      {helpContent[context]}
    </div>
  </div>
);`,
            },
            {
              title: "Recent Items",
              description: "Showing recently used items for quick access",
              code: `const RecentItems = () => {
  const [recentItems, setRecentItems] = useState([]);
  
  return (
    <div className="recent-items">
      <h3>Recently Viewed</h3>
      {recentItems.map(item => (
        <RecentItemCard key={item.id} item={item} />
      ))}
    </div>
  );
};`,
            },
          ],
          notes:
            "Reduce cognitive load by making information visible and accessible.",
        },
        {
          title: "6. Flexibility & Efficiency",
          content: "Accommodating both novice and expert users.",
          examples: [
            {
              title: "Keyboard Shortcuts",
              description: "Providing shortcuts for power users",
              code: `const ShortcutManager = () => {
  const shortcuts = {
    'Ctrl+S': () => saveDocument(),
    'Ctrl+Z': () => undo(),
    'Ctrl+Y': () => redo()
  };
  
  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, []);
};`,
            },
            {
              title: "Customizable Interface",
              description: "Allowing users to personalize their experience",
              code: `const UserPreferences = () => {
  const [preferences, setPreferences] = useState({
    theme: 'light',
    fontSize: 'medium',
    layout: 'default'
  });
  
  return (
    <div className="preferences-panel">
      <ThemeSelector value={preferences.theme} />
      <FontSizeSelector value={preferences.fontSize} />
      <LayoutSelector value={preferences.layout} />
    </div>
  );
};`,
            },
          ],
          notes:
            "Provide multiple ways to accomplish tasks for different user skill levels.",
        },
        {
          title: "7. Aesthetic & Minimalist Design",
          content:
            "Keeping interfaces clean and focused on essential elements.",
          examples: [
            {
              title: "Content Hierarchy",
              description: "Clear visual hierarchy of information",
              code: `const ContentLayout = () => (
  <div className="content-container">
    <h1 className="main-title">Primary Heading</h1>
    <p className="lead-text">Important summary text</p>
    <div className="content-body">
      <h2>Secondary Heading</h2>
      <p>Supporting content</p>
    </div>
  </div>
);`,
            },
            {
              title: "White Space",
              description: "Using space to create visual breathing room",
              code: `const SpacingSystem = {
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px'
};

const Card = ({ children }) => (
  <div className="card" style={{ padding: SpacingSystem.lg }}>
    {children}
  </div>
);`,
            },
          ],
          notes: "Remove unnecessary elements and focus on what's important.",
        },
        {
          title: "8. Help & Documentation",
          content: "Providing clear help and documentation when needed.",
          examples: [
            {
              title: "Onboarding Tour",
              description: "Guided tour of key features",
              code: `const OnboardingTour = () => {
  const steps = [
    {
      target: '.feature-1',
      content: 'This is where you can start your first task'
    },
    {
      target: '.feature-2',
      content: 'Track your progress here'
    }
  ];
  
  return <Tour steps={steps} />;
};`,
            },
            {
              title: "Contextual Documentation",
              description: "Help content that appears when needed",
              code: `const HelpCenter = () => (
  <div className="help-center">
    <SearchBar placeholder="Search help articles..." />
    <div className="help-categories">
      <CategoryCard title="Getting Started" />
      <CategoryCard title="Advanced Features" />
      <CategoryCard title="Troubleshooting" />
    </div>
  </div>
);`,
            },
          ],
          notes: "Make help easily accessible but not intrusive.",
        },
      ],
    },
  ],
};

export default uxuiCheatSheet;
